import {
  Button,
  Checkbox,
  CheckboxGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from "@nextui-org/react";
import React, { useState } from "react";
import { exportDownloadHandler } from "../../utils/helper";

/**
 * ExportModal Component
 * Modal for exporting metric response data in various formats
 * Allows users to select export format and specific fields to include
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onOpen - Function to open the modal
 * @param {Function} props.onOpenChange - Function to handle modal open/close state changes
 * @param {Array} props.data - Array of metric response data to export
 * @param {string} props.metricName - Name of the metric for file naming
 * @param {Array} props.formFieldsList - List of available form fields for selection
 * @returns {JSX.Element} Export configuration modal
 */
const ExportModal = ({
  isOpen,
  onOpen,
  onOpenChange,
  data,
  metricName,
  formFieldsList,
}) => {
  // State for selected fields and export format
  const [selectedFields, setSelectedFields] = useState([]);
  const [downloadFormat, setDownloadFormat] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  /**
   * Handle export submission
   * Validates required fields and triggers the export process
   * @param {Function} onClose - Function to close the modal
   */
  const submitHandler = (onClose) => {
    if (downloadFormat.length === 0) {
      setErrorMessage("This field is required.");
      return;
    }
    
    // Trigger export with selected format and fields
    exportDownloadHandler(metricName, downloadFormat[0], data, selectedFields);
    // Note: Modal is kept open to allow multiple exports
  };

  /**
   * Handle modal open/close state changes
   * Resets form state when modal is closed
   * @param {boolean} isOpen - Whether modal is open
   */
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setSelectedFields([]);
      setDownloadFormat([]);
      setErrorMessage("");
    }
    onOpenChange(isOpen); // Call the original onOpenChange
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleOpenChange} size="2xl">
      <ModalContent>
        {(onClose) => (
          <>
            {/* Modal Header */}
            <ModalHeader className="flex flex-col gap-1">
              Export the Responses
            </ModalHeader>
            
            {/* Modal Body */}
            <ModalBody>
              <div className="max-h-[400px] hide-scrollbar overflow-auto">
                {/* Export Format Selection */}
                <Select
                  label="Downloaded As"
                  placeholder=" "
                  variant="faded"
                  selectionMode="single"
                  labelPlacement="outside"
                  className="my-8"
                  isInvalid={errorMessage !== ""}
                  errorMessage={errorMessage}
                  selectedKeys={new Set(downloadFormat)}
                  onSelectionChange={(event) => {
                    setDownloadFormat(Array.from(event));
                  }}
                >
                  <SelectItem key="Excel" value="Excel" className="py-2">
                    Excel
                  </SelectItem>
                  <SelectItem key="CSV" value="CSV" className="py-2">
                    CSV
                  </SelectItem>
                  <SelectItem key="JSON" value="JSON" className="py-2">
                    JSON
                  </SelectItem>
                </Select>

                {/* Field Selection */}
                <div className="flex flex-col gap-4">
                  {/* Select All Checkbox */}
                  <Checkbox
                    color="secondary"
                    isIndeterminate={selectedFields.length > 0 && selectedFields.length < formFieldsList?.length}
                    isSelected={selectedFields.length === formFieldsList?.length}
                    onValueChange={(value) => {
                      if (value) {
                        setSelectedFields([...formFieldsList]);
                      } else {
                        setSelectedFields([]);
                      }
                    }}
                  >
                    Select All the fields
                  </Checkbox>
                  
                  {/* Individual Field Checkboxes */}
                  <CheckboxGroup
                    value={selectedFields}
                    onValueChange={(value) => {
                      setSelectedFields(value);
                    }}
                    className="pl-6"
                  >
                    {formFieldsList?.map((item) => (
                      <Checkbox key={item} color="secondary" value={item}>
                        {item}
                      </Checkbox>
                    ))}
                  </CheckboxGroup>
                </div>
              </div>
            </ModalBody>
            
            {/* Modal Footer */}
            <ModalFooter>
              <Button 
                variant="ghost" 
                className="text-slate-600 hover:text-slate-900 px-4" 
                onPress={onClose}
              >
                Cancel
              </Button>
              <Button 
                color="primary" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 font-medium"
                onClick={() => submitHandler(onClose)}
              >
                Download
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ExportModal;
